(ns app.domain.inspection.templates-config
  (:require #?(:clj [app.domain.application.condition.pdf :as condition-pdf])
            #?(:clj [app.domain.inspection.pdf-placeholder-replacements :as placeholder-replacements])
            [app.domain.application.condition.model :as condition-model]
            [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.party.model :as party-model]
            [app.domain.template.model :as template-model]))


(defn parties-with-the-role [visit role]
  (->> visit
       :entity/parent
       :entity/parent
       :entity/_parent
       (filter #(and (party-model/is-party? %)
                     (party-model/active? %)
                     (party-model/has-any-role? #{role} %)))
       (map party-model/merge-user-and-party)))


(defn all-parties [visit]
  (->> visit
       :entity/parent
       :entity/parent
       :entity/_parent
       (filter #(and (party-model/is-party? %)
                     (party-model/active? %)))
       (map party-model/merge-user-and-party)))

(defn all-photos [visit]
  (->> visit
       inspection-model/visit->inspection
       inspection-model/inspection-photos
       (sort-by :entity/created)))

;;
;; Template variables for order PDF and its snippets
;;

(def order-template-variables
  (-> ["header"
       "permit-number"
       "table-for-linked-checklists"
       "table-for-workspace-checklists"
       "deficiencies"
       "table-for-conditions"
       "linked-orders"
       "other-items"
       "parties"
       "date-issued"
       "issued-to"
       "address"]
      (into template-model/address-placeholders)
      (into ["address-information"
             "compliance-date"
             "municipality-name"
             "inspector-name"
             "inspector-phone"
             "inspector-bcin"
             "inspector-notes"
             "legal-description"
             "form-data"
             "type"])))

(defn order-picker-variables
  "Hide unit-related placeholders when `:feature-flag.id/property-db-units` is off."
  [feature-flags]
  (template-model/filter-unit-placeholders order-template-variables feature-flags))


(defn order-deficiency-snippet-variables [feature-flags]
  (into [] (concat
             ["description"]
             (when (:feature-flag.id/enhanced-template-configuration feature-flags)
               ["deficiency-number"
                "related-photos"])
             ["required-action"]
             ["comply-by-date"])))


(def order-order-snippet-variables
  ["type"
   "issue-date"])


(def order-other-snippet-variables
  ["item-number"
   "reference"
   "description"])

(def order-party-snippet-variables
  template-model/party-placeholders)

;;
;; Template variables for 'actual' inspection PDFs and their snippets
;;

(def occupancy-permit-template-variables
  (-> [{:variable-name "header"}
       {:variable-name "applicants" :snippet? true}
       {:variable-name "area-to-be-occupied"}
       {:variable-name "area-to-be-occupied-m2"}
       {:variable-name "area-to-be-occupied-ft2"}
       {:variable-name "builders" :snippet? true}
       {:variable-name "date-of-inspection"}
       {:variable-name "expected-date-of-occupancy"}
       {:variable-name "inspector-bcin"}
       {:variable-name "inspector-name"}
       {:variable-name "inspector-phone-number"}
       {:variable-name "legal-description"}
       {:variable-name "municipality-name"}
       {:variable-name "municipal-address"}]
      (into (template-model/as-variable-name-maps template-model/address-placeholders))
      (into [{:variable-name "occupancy-granted-date"}
             {:variable-name "occupancy-permit-description"}
             {:variable-name "occupancy-table-for-deficiencies" :snippet? true}
             {:variable-name "no-deficiencies-text" :snippet? true}
             {:variable-name "outstanding-matters"}
             {:variable-name "owners" :snippet? true}
             {:variable-name "partial-occupancy-request-description"}
             {:variable-name "permit-description"}
             {:variable-name "permit-issue-date"}
             {:variable-name "permit-number"}
             {:variable-name "permit-type"}
             {:variable-name "property-attributes"}
             {:variable-name "roll-number"}
             {:variable-name "scope-of-work"}
             {:variable-name "visit-result"}
             {:variable-name "form-data"}])))


(def party-snippet-variables
  (template-model/as-variable-name-maps template-model/party-placeholders))

(defn photo-snippet-variables [feature-flags]
  (into [] (concat
             [{:variable-name "date"}]
             [{:variable-name "description"}]
             (when (:feature-flag.id/enhanced-template-configuration feature-flags)
               [{:variable-name "photo-number"}])
             [{:variable-name "photo"}]
             [{:variable-name "related-deficiencies"}])))

(defn inspection-report-template-variables [group feature-flags]
  (-> [{:variable-name "header"}
       {:variable-name "permit-issue-date"}
       {:variable-name "permit-number"}
       {:variable-name "permit-type"}
       {:variable-name "inspection-name"}
       {:variable-name "inspection-status"}
       {:variable-name "work-task-name"}
       {:variable-name "work-task-status"}
       {:variable-name "legal-description"}
       {:variable-name "municipality-name"}
       {:variable-name "municipal-address"}]
      (into (template-model/as-variable-name-maps template-model/address-placeholders))
      (into [{:variable-name "property-attributes"}
             {:variable-name "table-for-parties" :snippet? true}
             {:variable-name "no-parties-text" :snippet? true}
             {:variable-name "table-for-photos" :snippet? true}])
      (cond-> (inspection-model/has-checklists-v2? group feature-flags)
        (into [{:variable-name "table-for-checklists" :snippet? true}]))
      (into [{:variable-name "table-for-deficiencies" :snippet? true}
             {:variable-name "no-deficiencies-text" :snippet? true}
             {:variable-name "table-for-conditions" :snippet? true}
             {:variable-name "table-for-visits" :snippet? true}
             {:variable-name "licence-number"}
             {:variable-name "category"}
             {:variable-name "work-target"}
             {:variable-name "work-type"}
             {:variable-name "form-data"}])))

(def inspection-report-licence-template-variables
  #{"licence-number"})

(def inspection-report-inspection-variables
  #{"inspection-name" "inspection-status"})

(def inspection-report-work-order-variables
  #{"work-task-name" "work-task-status"})

(defn code-enforcement-inspection-report-template-variables [group feature-flags]
  (-> [{:variable-name "header"}
       {:variable-name "case-number"} ; alias for "permit-number"
       {:variable-name "inspection-name"}
       {:variable-name "inspection-status"}
       {:variable-name "legal-description"}
       {:variable-name "municipality-name"}
       {:variable-name "municipal-address"}]
      (into (template-model/as-variable-name-maps template-model/address-placeholders))
      (into [{:variable-name "table-for-parties" :snippet? true}
             {:variable-name "no-parties-text" :snippet? true}])
      (cond-> (inspection-model/enforcement-report-has-checklists? group feature-flags)
        (into [{:variable-name "table-for-checklists" :snippet? true}]))
      (into [{:variable-name "table-for-deficiencies" :snippet? true}
             {:variable-name "no-deficiencies-text" :snippet? true}
             {:variable-name "table-for-visits" :snippet? true}
             {:variable-name "case-description"}
             {:variable-name "form-data"}
             {:variable-name "table-for-photos" :snippet? true}])))

(def visit-snippet-variables
  [{:variable-name "visit-date"}
   {:variable-name "visit-time"}
   {:variable-name "visit-result"}
   {:variable-name "visit-failure-reason"}
   {:variable-name "visit-site-conditions"}
   {:variable-name "inspectors" :snippet? true}])

(def visit-table-variables
  [{:variable-name "visit-items" :snippet? true}])

(def checklist-table-variables
  [{:variable-name "checklists" :snippet? true}])

(def deficiency-table-variables
  [{:variable-name "deficiency-items" :snippet? true}])

(def occupancy-deficiency-table-variables
  [{:variable-name "occupancy-deficiency-items" :snippet? true}])

(def party-table-variables
  [{:variable-name "parties" :snippet? true}])

(def table-for-photos-variables
  [{:variable-name "photos" :snippet? true}])

(def inspector-snippet-variables
  [{:variable-name "inspector-name"}
   {:variable-name "inspector-phone-number"}])

(def checklist-item-snippet-variables
  (into [] (concat
             [{:variable-name "pass-result"}]
             [{:variable-name "fail-result"}]
             [{:variable-name "na-result"}]
             [{:variable-name "description"}]
             [{:variable-name "note"}]
             [{:variable-name "reference"}])))

(def checklist-group-snippet-variables
  [{:variable-name "checklist-group-name"}
   {:variable-name "positive-label"}
   {:variable-name "negative-label"}
   {:variable-name "na-label"}
   {:variable-name "checklist-items" :snippet? true}])

(def checklist-snippet-variables
  [{:variable-name "checklist-name"}
   {:variable-name "checklist-groups" :snippet? true}])

(defn deficiency-snippet-variables [group feature-flags]
  (into [] (concat
             [{:variable-name "description"}]
             [{:variable-name "discovery-date"}]
             [{:variable-name "compliance-date"}]
             [{:variable-name "reference"}]
             (when (:feature-flag.id/enhanced-template-configuration feature-flags)
               [{:variable-name "deficiency-number"}])
             (when (group-model/has-feature? group :group.feature/comply-by-date)
               [{:variable-name "comply-by-date"}])
             [{:variable-name "related-photos"}]
             (when (group-model/has-feature? group :group.feature/inspection-labels)
               [{:variable-name "labels"}]))))

#_{:clj-kondo/ignore #?(:cljs [:unused-binding] :clj [])}
(defn snippet-template-for-role [hide-html-editor? role template-ref]
  (merge {:db-reference       :inspection.pdf-template/party-snippet
          :template-reference template-ref
          :template-variables (remove #(-> % :variable-name (= "person-roles")) party-snippet-variables)
          :pdf-title          :inspection.pdf-template/party-snippet-for-specific-roles
          :hide-html-editor?  hide-html-editor?}
         #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-party-placeholder
                   :entity->snippet-entities-fn #(parties-with-the-role % role)}
            :cljs {})))


(def applicant-snippet-template
  (snippet-template-for-role false :role/applicant "applicants"))


(def builder-snippet-template
  (snippet-template-for-role true :role/builder "builders"))


(def owner-snippet-template
  (snippet-template-for-role true :role/property-owner "owners"))


(def inspector-snippet
  (merge {:db-reference        :inspection.pdf-template/inspector-snippet
          :template-variables  inspector-snippet-variables
          :pdf-title           :inspection.pdf-template/inspector-snippet
          :template-reference  "inspectors"
          :display-html-editor true}
         #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-inspector-placeholder
                   :entity->snippet-entities-fn (fn [visit]
                                                  (-> visit
                                                      :inspection.visit/inspectors))}
            :cljs {})))


(def visit-item-snippet
  (merge {:db-reference        :inspection.pdf-template/visit-snippet
          :template-variables  visit-snippet-variables
          :snippets            [inspector-snippet]
          :pdf-title           :inspection.pdf-template/visit-snippet
          :template-reference  "visit-items"
          :display-html-editor true}
         #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-visit-placeholder
                   :entity->snippet-entities-fn (fn [visit]
                                                  (->> visit
                                                       :entity/parent
                                                       inspection-model/inspection-visits
                                                       (sort-by :inspection.visit/start-time)))}
            :cljs {})))


(def table-for-visits
  (merge {:db-reference        :inspection.pdf-template/table-for-visits-snippet
          :template-variables  visit-table-variables
          :snippets            [visit-item-snippet]
          :pdf-title           :inspection.pdf-template/table-for-visits-snippet
          :template-reference  "table-for-visits"
          :display-html-editor true}
         #?(:clj  {:entity->snippet-entities-fn (fn [visit] [visit])}
            :cljs {})))


(defn checklist-item-snippet []
  (merge {:fragment-reference :inspection.pdf-template/checklist-item-snippet
          :template-variables checklist-item-snippet-variables
          :template-reference "checklist-items"
          :pdf-title          :inspection.pdf-template/checklist-item-snippet}
         #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-checklist-item-placeholder
                   :entity->snippet-entities-fn (fn [checklist-group] (->> checklist-group
                                                                           inspection-model/checklist-group-items
                                                                           (sort-by :ordered/order)))}
            :cljs {})))


(defn checklist-group-snippet
  ([] (checklist-group-snippet (checklist-item-snippet)))
  ([& subsnippets]
   (merge {:fragment-reference :inspection.pdf-template/checklist-group-snippet
           :template-variables checklist-group-snippet-variables
           :template-reference "checklist-groups"
           :snippets           subsnippets
           :pdf-title          :inspection.pdf-template/checklist-group-snippet}
          #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-checklist-group-placeholder
                    :entity->snippet-entities-fn (fn [checklist] (->> checklist
                                                                      inspection-model/checklist-groups
                                                                      (sort-by :ordered/order)))}
             :cljs {}))))


(defn checklist-snippet
  ([] (checklist-snippet (checklist-group-snippet)))
  ([& subsnippets]
   (merge {:fragment-reference :inspection.pdf-template/checklist-snippet
           :template-variables checklist-snippet-variables
           :template-reference "checklists"
           :snippets           subsnippets
           :pdf-title          :inspection.pdf-template/checklist-snippet}
          #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-checklist-placeholder
                    :entity->snippet-entities-fn (fn [checklists] (->> checklists
                                                                       (sort-by :ordered/order)))}
             :cljs {}))))


(defn table-for-checklists
  ([] (table-for-checklists (checklist-snippet)))
  ([& subsnippets]
   (merge {:fragment-reference :inspection.pdf-template/table-for-checklists-snippet
           :template-reference "table-for-checklists"
           :template-variables checklist-table-variables
           :snippets           subsnippets
           :pdf-title          :inspection.pdf-template/table-for-checklists-snippet}
          #?(:clj  {:entity->snippet-entities-fn (fn [visit]
                                                   (when-let [checklists (->> visit
                                                                              :entity/parent
                                                                              inspection-model/checklists
                                                                              seq)]
                                                     [checklists]))}
             :cljs {}))))


(defn empty-checklist-table []
  (merge {:fragment-reference :inspection.pdf-template/empty-checklist-table-snippet
          :template-reference "table-for-checklists"
          :template-variables []
          :snippets           []
          :pdf-title          :inspection.pdf-template/table-for-checklists-snippet}
         #?(:clj  {:entity->snippet-entities-fn identity}
            :cljs {})))


(defn deficiency-item-snippet [group feature-flags]
  (merge {:db-reference       :inspection.pdf-template/deficiency-snippet
          :template-variables (deficiency-snippet-variables group feature-flags)
          :template-reference "deficiency-items"
          :pdf-title          :inspection.pdf-template/deficiency-snippet}
         #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-deficiency-placeholder
                   :entity->snippet-entities-fn (fn [visit] (->> visit
                                                                 :entity/parent
                                                                 inspection-model/actual-deficiencies
                                                                 (sort-by :entity/created)))}
            :cljs {})))


(defn table-for-deficiencies [group feature-flags]
  (merge {:db-reference       :inspection.pdf-template/table-for-deficiencies-snippet
          :template-reference "table-for-deficiencies"
          :template-variables deficiency-table-variables
          :snippets           [(deficiency-item-snippet group feature-flags)]
          :pdf-title          :inspection.pdf-template/table-for-deficiencies-snippet}
         #?(:clj  {:entity->snippet-entities-fn (fn [visit]
                                                  (when (->> visit
                                                             :entity/parent
                                                             inspection-model/actual-deficiencies
                                                             seq)
                                                    [visit]))}
            :cljs {})))


(defn occupancy-permit-deficiency-item-snippet [group feature-flags]
  (merge {:db-reference       :inspection.pdf-template/deficiency-snippet
          :template-variables (deficiency-snippet-variables group feature-flags)
          :template-reference "occupancy-deficiency-items"
          :pdf-title          :inspection.pdf-template/deficiency-snippet}
         #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-deficiency-placeholder
                   :entity->snippet-entities-fn (fn [visit] (->> visit
                                                                 :entity/parent
                                                                 inspection-model/active-deficiencies
                                                                 (sort-by :entity/created)))}
            :cljs {})))


(defn occupancy-table-for-deficiencies [group feature-flags]
  (merge {:db-reference       :inspection.pdf-template/table-for-deficiencies-snippet
          :template-reference "occupancy-table-for-deficiencies"
          :template-variables occupancy-deficiency-table-variables
          :snippets           [(occupancy-permit-deficiency-item-snippet group feature-flags)]
          :pdf-title          :inspection.pdf-template/table-for-deficiencies-snippet}
         #?(:clj  {:entity->snippet-entities-fn (fn [visit]
                                                  (when (->> visit
                                                             :entity/parent
                                                             inspection-model/active-deficiencies
                                                             seq)
                                                    [visit]))}
            :cljs {})))


(def no-deficiencies-snippet
  (merge {:db-reference       :inspection.pdf-template/no-deficiencies-snippet
          :template-reference "no-deficiencies-text"
          :pdf-title          :inspection.pdf-template/no-deficiencies-snippet}
         #?(:clj  {:entity->snippet-entities-fn (fn [visit]
                                                  (when (->> visit
                                                             :entity/parent
                                                             inspection-model/actual-deficiencies
                                                             empty?)
                                                    [""]))}
            :cljs {})))


(def condition-item-snippet
  (merge {:fragment-reference :inspection.pdf-template/condition-snippet
          :template-reference "condition-items"
          :template-variables condition-model/condition-snippet-variables
          :pdf-title          :inspection.pdf-template/condition-snippet}
         #?(:clj  {:replace-placeholder-fn      condition-pdf/replace-condition-placeholder
                   :entity->snippet-entities-fn condition-pdf/condition-snippet-data}
            :cljs {})))

(def condition-table-snippet
  (merge {:fragment-reference :inspection.pdf-template/condition-table-snippet
          :template-reference "table-for-conditions"
          :template-variables condition-model/condition-snippet-table-variables
          :snippets           [condition-item-snippet]
          :pdf-title          :inspection.pdf-template/condition-table-snippet}
         #?(:clj  {:entity->snippet-entities-fn (fn [visit]
                                                  (when-let [phase-entity (some-> visit
                                                                                  :entity/parent
                                                                                  inspection-model/inspection->phase)]
                                                    (when (seq (condition-pdf/condition-snippet-data phase-entity))
                                                      [phase-entity])))}
            :cljs {})))

(def empty-condition-table-snippet
  {:fragment-reference          :inspection.pdf-template/empty-condition-table-snippet
   :template-reference          "table-for-conditions"
   :template-variables          []
   :snippets                    []
   :pdf-title                   :inspection.pdf-template/condition-table-snippet
   :hide-html-editor?           true
   :entity->snippet-entities-fn identity})

(defn order-condition-snippets [table-reference]
  (let [selection [:pdf-title
                   :template-reference
                   :template-variables
                   :replace-placeholder-fn
                   :entity->snippet-entities-fn]]
    (-> condition-table-snippet
        (select-keys selection)
        (assoc :db-reference                :order.template/condition-table-snippet
               :template-reference          table-reference
               :snippets                    [(-> condition-item-snippet
                                                 (select-keys selection)
                                                 (assoc :db-reference :order.template/condition-snippet))])
        (merge #?(:clj  {:entity->snippet-entities-fn (fn [phase-entity]
                                                        (when (seq (condition-pdf/condition-snippet-data phase-entity))
                                                          [phase-entity]))}
                  :cljs {})))))


(def party-item-snippet
  (merge {:db-reference       :inspection.pdf-template/party-snippet
          :template-reference "parties"
          :template-variables party-snippet-variables
          :pdf-title          :inspection.pdf-template/party-snippet}
         #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-party-placeholder
                   :entity->snippet-entities-fn all-parties}
            :cljs {})))


(def table-for-parties
  (merge {:db-reference       :inspection.pdf-template/table-for-parties-snippet
          :template-reference "table-for-parties"
          :template-variables party-table-variables
          :snippets           [party-item-snippet]
          :pdf-title          :inspection.pdf-template/table-for-parties-snippet}
         #?(:clj  {:entity->snippet-entities-fn (fn [visit]
                                                  (when (->> visit
                                                             all-parties
                                                             seq)
                                                    [visit]))}
            :cljs {})))


(def no-parties-snippet
  (merge {:db-reference       :inspection.pdf-template/no-parties-snippet
          :template-reference "no-parties-text"
          :pdf-title          :inspection.pdf-template/no-parties-snippet}
         #?(:clj  {:entity->snippet-entities-fn (fn [visit]
                                                  (when (->> visit
                                                             all-parties
                                                             empty?)
                                                    [""]))}
            :cljs {})))

(defn photo-item-snippet [feature-flags]
  (merge {:fragment-reference :inspection.pdf-template/photo-snippet
          :template-reference "photos"
          :template-variables (photo-snippet-variables feature-flags)
          :pdf-title          :inspection.pdf-template/photo-snippet}
         #?(:clj  {:replace-placeholder-fn      placeholder-replacements/replace-photo-placeholder
                   :entity->snippet-entities-fn all-photos}
            :cljs {})))

(defn table-for-photos [feature-flags]
  (merge {:fragment-reference :inspection.pdf-template/table-for-photos-snippet
          :template-reference "table-for-photos"
          :template-variables table-for-photos-variables
          :snippets           [(photo-item-snippet feature-flags)]
          :pdf-title          :inspection.pdf-template/table-for-photos-snippet}
         #?(:clj  {:entity->snippet-entities-fn (fn [visit]
                                                  (when (->> visit
                                                             all-photos
                                                             seq)
                                                    [visit]))}
            :cljs {})))

(defn order-checklist-snippets [table-reference]
  (let [ref-attr     :db-reference
        override-cfg (fn [cfg-fn snippet-ref & children]
                       (-> (apply cfg-fn children)
                           (dissoc :db-reference :fragment-reference)
                           (assoc ref-attr snippet-ref
                                  :pdf-title snippet-ref)))]
    (->> {:template-reference table-reference}
         (merge
           (override-cfg table-for-checklists
                         :order.template/checklist-table-snippet
                         (override-cfg checklist-snippet
                                       :order.template/checklist-snippet
                                       (override-cfg checklist-group-snippet
                                                     :order.template/checklist-group-snippet
                                                     (override-cfg checklist-item-snippet
                                                                   :order.template/checklist-item-snippet))))))))

(defn occupancy-permit-template-config [group feature-flags]
  (merge
    {:template-variables occupancy-permit-template-variables
     :snippets           [applicant-snippet-template
                          builder-snippet-template
                          owner-snippet-template
                          (occupancy-table-for-deficiencies group feature-flags)
                          no-deficiencies-snippet]
     :pdf-title          :inspection.pdf-template.type/occupancy-permit}
    #?(:clj  {:replace-placeholder-fn placeholder-replacements/replace-visit-placeholder}
       :cljs {})))


(defn general-report-template-config [group feature-flags]
  (merge
    {:template-variables (inspection-report-template-variables group feature-flags)
     :snippets           (concat
                           (if (inspection-model/has-checklists-v2? group feature-flags)
                             [(table-for-checklists)]
                             [(empty-checklist-table)])
                           (if (condition-model/template-snippets-enabled? group feature-flags)
                             [condition-table-snippet]
                             [empty-condition-table-snippet])
                           [(table-for-deficiencies group feature-flags)
                            no-deficiencies-snippet
                            table-for-visits
                            table-for-parties
                            no-parties-snippet
                            (table-for-photos feature-flags)])
     :pdf-title          :inspection.pdf-template.type/general-report}
    #?(:clj  {:replace-placeholder-fn placeholder-replacements/replace-visit-placeholder}
       :cljs {})))

(defn code-enforcement-template-config [group feature-flags]
  (merge
    {:template-variables (code-enforcement-inspection-report-template-variables group feature-flags)
     :snippets           (concat
                           (if (inspection-model/enforcement-report-has-checklists? group feature-flags)
                             [(table-for-checklists)]
                             [(empty-checklist-table)])
                           [(table-for-deficiencies group feature-flags)
                            no-deficiencies-snippet
                            table-for-visits
                            table-for-parties
                            no-parties-snippet
                            (table-for-photos feature-flags)])
     :pdf-title          :inspection.pdf-template.type/code-enforcement-report}
    #?(:clj  {:replace-placeholder-fn placeholder-replacements/replace-visit-placeholder}
       :cljs {})))


(defn pdf-template-configs [group feature-flags]
  {:inspection.pdf-template.type/occupancy-permit        (occupancy-permit-template-config group feature-flags)
   :inspection.pdf-template.type/general-report          (general-report-template-config group feature-flags)
   :inspection.pdf-template.type/code-enforcement-report (code-enforcement-template-config group feature-flags)})


(defn variable-names-wo-snippet-variables
  [template-variables]
  (->> template-variables
       (remove :snippet?)
       (map :variable-name)))


(def pdf-template-types
  (vec (keys (pdf-template-configs nil #{}))))


(defn available-pdf-template-types-for-group [group]
  (case (group-model/primary-permit-type group)
    :permit-type/PWO [:inspection.pdf-template.type/general-report]
    pdf-template-types))
